"use client";

import { useRef } from "react";
import { ModalOverlay } from "../atoms/modal-overlay";
import { useModalShell } from "@/hooks/use-modal-shell";

type ModalSize = "sm" | "md" | "lg" | "xl";

interface ModalTemplateProps {
  open: boolean;
  onClose: () => void;
  /**
   * Touche Échap, quand elle ne doit pas tout fermer. Une modale qui a une
   * étape précédente à l'écran veut y revenir plutôt que disparaître ; la
   * croix et le fond, eux, ferment toujours. Par défaut : `onClose`.
   */
  onEscape?: () => void;
  title: string;
  /** Accessible title, shown to screen readers when titleHidden is true */
  titleHidden?: boolean;
  /** Optional element rendered next to the title (e.g. subtitle text) */
  titleExtra?: React.ReactNode;
  /** Optional icon rendered before the title */
  titleIcon?: React.ReactNode;
  size?: ModalSize;
  maxHeight?: string;
  header?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  error?: string | null;
}

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: "sm:w-[400px] sm:max-w-[90vw]",
  md: "sm:w-[480px] sm:max-w-[90vw]",
  lg: "sm:w-[720px] sm:max-w-[92vw]",
  xl: "sm:w-[960px] sm:max-w-[92vw]",
};

/**
 * Generic modal shell, aligned on the Upload Modal V2 language :
 * - Strong glass backdrop + 10px blur (handled by `ModalOverlay`).
 * - 16px radius card with the layered violet-tinted premium shadow stack.
 * - Tokenised borders / text colors, no hard-coded white opacities here.
 * - Keyboard trap so screen-reader & keyboard users stay inside the dialog
 *   while it's open, plus `Escape` to dismiss.
 *
 * Visual choices kept from the previous version because they match V2 :
 * the gradient title, the SVG noise overlay and the multi-layer shadow.
 */
export function ModalTemplate({
  open,
  onClose,
  onEscape,
  title,
  titleHidden = false,
  titleExtra,
  titleIcon,
  size = "md",
  maxHeight = "85vh",
  header,
  children,
  footer,
  error,
}: ModalTemplateProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  // Piege de focus, Escape, focus a l ouverture. Partage avec l ecran de
  // compte, qui a le meme comportement clavier sans le meme habillage.
  useModalShell(modalRef, open, onEscape ?? onClose);

  if (!open) {
    return null;
  }

  return (
    <ModalOverlay onClick={onClose}>
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={`relative w-full ${SIZE_CLASSES[size]} flex flex-col rounded-2xl overflow-hidden outline-none`}
        style={{
          backgroundColor: "#1a1a2a",
          border: "1px solid var(--color-glass-border)",
          boxShadow: [
            "0 1px 2px rgba(0, 0, 0, 0.20)",
            "0 4px 8px rgba(0, 0, 0, 0.18)",
            "0 12px 24px rgba(0, 0, 0, 0.16)",
            "0 24px 48px rgba(0, 0, 0, 0.14)",
            "0 0 24px rgba(139, 92, 246, 0.08)",
            "inset 0 1px 0 rgba(255, 255, 255, 0.06)",
          ].join(", "),
          maxHeight,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Subtle noise texture, gives the surface a hint of "frosted glass" feel on top of the backdrop blur */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
            opacity: 0.04,
            mixBlendMode: "overlay",
          }}
        />

        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 shrink-0 relative"
          style={{ borderBottom: "1px solid var(--color-glass-separator)" }}
        >
          <div className="flex items-center gap-2">
            {titleIcon}
            {titleHidden ? (
              <span className="sr-only">{title}</span>
            ) : (
              <span
                className="text-[15px] font-semibold"
                style={{
                  background: "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.70) 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {title}
              </span>
            )}
            {titleExtra}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/[0.06] hover:rotate-90 active:scale-95 transition-all duration-200 cursor-pointer"
            style={{ color: "var(--color-text-secondary)" }}
            aria-label="Fermer"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>

        {/* Optional sub-header (step dots, etc.) */}
        {header && (
          <div
            className="px-6 py-3 shrink-0 relative"
            style={{ borderBottom: "1px solid var(--color-glass-separator)" }}
          >
            {header}
          </div>
        )}

        {/* Content area */}
        <div className="flex-1 min-h-0 flex flex-col overflow-y-auto relative">
          {children}
        </div>

        {/* Error */}
        {error && (
          <div className="px-6 pb-2 relative">
            <p className="text-[11px]" style={{ color: "var(--color-err)" }}>{error}</p>
          </div>
        )}

        {/* Footer */}
        {footer && (
          <div
            className="flex items-center justify-between px-6 py-4 shrink-0 relative"
            style={{ borderTop: "1px solid var(--color-glass-separator)" }}
          >
            {footer}
          </div>
        )}
      </div>
    </ModalOverlay>
  );
}
